import { Component } from 'preact'
import axios from 'axios'
import TopicListItemView, { TopicInfo } from './TopicListItemView.sync'
import Analytics from '../analytics/Analytics'
import { common } from '../common'
import Phrase from '../../libs/translationsUtils/phrase'

export interface QueryType {
  sort: string
  sortOrder: string
  minReplies: number
  maxResults: number
  contentType?: string
  hasBestAnswer?: boolean
}

export interface TopicsListViewProps {
  topics: TopicInfo[]
  query: QueryType
  requestUrl: string
  attachmentCdn: string
}

interface State {
  page: number
  isLoading?: boolean
  isDone?: boolean
  topics: TopicsListViewProps['topics']
}

interface TopicUnreadData {
  [topicId: string]: {
    postCount: string
  } | undefined
}

export default class TopicListView extends Component<TopicsListViewProps, State> {
  topicUnreadCache: TopicUnreadData = {}
  constructor (props: TopicsListViewProps) {
    super(props)

    this.state = {
      page: 1,
      topics: props.topics || []
    }

    this.getTopicData = this.getTopicData.bind(this)
    this.showMoreHandler = this.showMoreHandler.bind(this)
  }

  componentDidMount () {
    const { query } = this.props
    const newQuery = { ...query, page: 1 }

    if (!this.state.topics) return this.getTopicData(newQuery)
    if (typeof window === 'undefined' || !window.inSidedData.user.userid) return
    this.updateTopicReadStatus()
  }

  async updateTopicReadStatus () {
    const topicIds = this.state.topics.filter(topic => topic.numberOfUnreadReplies === undefined && !(topic.id in this.topicUnreadCache)).map(topic => topic.id)
    if (!topicIds.length) return
    const res = await axios.get<TopicUnreadData|false>('/topic/unreadPostDataPerForum', {
      params: { topicIds: JSON.stringify(topicIds) }
    })
    const { data } = res
    if (data === false) return
    this.topicUnreadCache = { ...this.topicUnreadCache, ...data }
    this.setState({
      topics: this.state.topics.map(topic => {
        if (topic.id in data) {
          // make sure unread replies is not bigger than total replies
          const numberOfUnreadRepliesIncludingTopic = Math.min(
            parseInt(data[topic.id]?.postCount || '0'),
            topic.numberOfReplies + 1
          )

          const isTopicOpenerUnread = numberOfUnreadRepliesIncludingTopic === topic.numberOfReplies + 1

          return {
            ...topic,
            numberOfUnreadReplies: numberOfUnreadRepliesIncludingTopic - (isTopicOpenerUnread ? 1 : 0),
            isTopicUnread: numberOfUnreadRepliesIncludingTopic > 0
          }
        }
        return { ...topic, isTopicUnread: false, numberOfUnreadReplies: 0 }
      })
    })
  }

  getTopicData (query: {page: number}) {
    const { requestUrl } = this.props
    const { topics } = this.state

    this.setState({
      isLoading: true
    })

    const topicDataCall = async () => {
      const res = await axios.get(requestUrl, {
        params: {
          query: JSON.stringify(query)
        }
      })

      const { data: newTopics } = res
      const newData = (topics || []).concat(newTopics)

      this.setState({
        topics: newData,
        page: query.page,
        isDone: newTopics.length < 10,
        isLoading: false
      })

      this.updateTopicReadStatus()
    }

    topicDataCall()
  }

  showMoreHandler () {
    const { query } = this.props
    const { page } = this.state
    const newQuery = { ...query, page: page + 1 }

    Analytics.send('Homepage', 'Show more activity clicked')

    this.getTopicData(newQuery)
  }

  render () {
    const { attachmentCdn } = this.props
    const { topics, isDone, isLoading } = this.state
    let btnClass = 'btn--cta btn--full-width btn--load-more'
    let disabledButton = false

    if (!topics) {
      return null
    }

    if (isLoading) {
      btnClass += ' is-loading'
      disabledButton = true
    }

    return (
      <div className='list-widget-wrapper'>
        {
          topics.map(topic => {
            return (
              <TopicListItemView
                key={topic.id}
                topic={topic}
                attachmentCdn={attachmentCdn}
                hideCategoryMetadata={((topic.type === 'ideation' && common.isModuleActive('ideationV2')) || topic.contentType === 'productUpdate')}
              />
            )
          })
        }
        {
          !isDone
            ? (
              <div className='load-more-container load-more-topics' key='load-more'>
                <button className={btnClass} onClick={this.showMoreHandler} type='button' disabled={disabledButton}>
                  <Phrase p='Show more' />
                  <span className='loader' />
                </button>
              </div>
              )
            : null
        }
      </div>
    )
  }
}
